import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import DropDownList from "../../../components/Atomos/DropDown/Dropdown";
import RadioButton from "../../../components/Atomos/Radio/RadioButton";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import { SpeakerTypeEnum } from "../../../Enums/EventEnums";
import { UseFirmList } from "../../../hooks/Catalogs/useFirm";
import { fetchPersonByFirm } from "../../../Services/Person";
import { getCompanies } from "../../../Services/Company";
import { getGuestSpeakersByCompany } from "../../../Services/GuestSpeaker";
import PaginatedTable from "../../../components/Organismos/Table/PaginatedTable";
import { defaultPageSize } from "../../../utils/const";
import { ISorting } from "../../../interfaces/Components/ITable";
import Trash from "../../../components/Atomos/Icons/EditMode/Trash";
import PlusIcon from "../../../components/Atomos/Icons/PlusIcon";
import { toastError } from "../../../utils/toast";

export interface PresenterForm {
  personType: keyof typeof SpeakerTypeEnum;
  memberFirm: { id: string; name: string } | null;
  professional: { id: string; name: string } | null;
}

interface PresentersManagerProps {
  presenters: PresenterForm[];
  onPresentersChange: (presenters: PresenterForm[]) => void;
  title?: string;
  route?: string;
}

const PresentersManager: React.FC<PresentersManagerProps> = ({
  presenters,
  onPresentersChange,
  title = "Session Presenters",
  route = "SessionPresenters",
}) => {
  const { firmData } = UseFirmList();
  const [formPresenter, setFormPresenter] = useState<{
    personType: keyof typeof SpeakerTypeEnum | null;
    memberFirm: { id: string; name: string } | null;
    professional: { id: string; name: string } | null;
  }>({
    personType: "speaker" as keyof typeof SpeakerTypeEnum,
    memberFirm: null,
    professional: null,
  });

  const [filteredProfessionals, setFilteredProfessionals] = useState<{ id: string; name: string; firm?: string }[]>([]);
  const [companyOptions, setCompanyOptions] = useState<{ id: string; name: string }[]>([]);
  const [filteredGuestSpeakers, setFilteredGuestSpeakers] = useState<{ id: string; name: string }[]>([]);
  const [sort, setSort] = useState<ISorting | undefined>(undefined);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });

  const prevFirmIdRef = useRef<string | null>(null);
  const prevPersonTypeRef = useRef<keyof typeof SpeakerTypeEnum | null>(null);
  const [dropdownResetKey, setDropdownResetKey] = useState(0);

  const speakerTypeOptions = useMemo(() => [
    { key: "speaker", value: SpeakerTypeEnum.speaker },
    { key: "keynote", value: SpeakerTypeEnum.keynote },
    { key: "moderator", value: SpeakerTypeEnum.moderator },
    { key: "panelist", value: SpeakerTypeEnum.panelist },
    { key: "guest", value: SpeakerTypeEnum.guest },
  ], []);

  const firmOptions = useMemo(() => firmData.map((firm: any) => ({
    id: firm.id,
    name: firm.name || firm.firmName || "Unknown Firm",
  })), [firmData]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const companies = await getCompanies();
        const formattedData = companies.map((company: any) => ({
          id: company.id,
          name: company.name || "Unknown Company",
        }));
        setCompanyOptions(formattedData);
      } catch (error) {
        console.error("Error fetching companies:", error);
        setCompanyOptions([]);
      }
    };

    fetchCompanies();
  }, []);

  useEffect(() => {
    const fetchProfessionalsByFirm = async () => {
      if (formPresenter?.personType === "guest" || !formPresenter?.memberFirm?.id) {
        setFilteredProfessionals([]);
        return;
      }

      try {
        const persons = await fetchPersonByFirm(formPresenter.memberFirm.id);
        const formattedData = persons.map((person: any) => ({
          id: person.id,
          name: person.displayName || `${person.name || ""} ${person.lastName || ""}`.trim() || "Unknown",
          firm: person.firm || formPresenter.memberFirm?.id,
        }));
        setFilteredProfessionals(formattedData);
      } catch (error) {
        console.error("Error fetching professionals by firm:", error);
        setFilteredProfessionals([]);
      }
    };

    fetchProfessionalsByFirm();
  }, [formPresenter?.memberFirm?.id, formPresenter?.personType]);

  useEffect(() => {
    const fetchGuestSpeakersByCompany = async () => {
      if (formPresenter?.personType !== "guest" || !formPresenter?.memberFirm?.id) {
        setFilteredGuestSpeakers([]);
        return;
      }

      try {
        const guestSpeakers = await getGuestSpeakersByCompany(formPresenter.memberFirm.id);
        const formattedData = guestSpeakers.map((guestSpeaker: any) => ({
          id: guestSpeaker.id,
          name: `${guestSpeaker.firstName || ""} ${guestSpeaker.lastName || ""}`.trim() || "Unknown",
        }));
        setFilteredGuestSpeakers(formattedData);
      } catch (error) {
        console.error("Error fetching guest speakers by company:", error);
        setFilteredGuestSpeakers([]);
      }
    };

    fetchGuestSpeakersByCompany();
  }, [formPresenter?.memberFirm?.id, formPresenter?.personType]);

  useEffect(() => {
    if (prevFirmIdRef.current !== formPresenter?.memberFirm?.id && formPresenter.professional) {
      setFormPresenter(prev => ({ ...prev, professional: null }));
    }
    prevFirmIdRef.current = formPresenter?.memberFirm?.id || null;
  }, [formPresenter?.memberFirm?.id, formPresenter.professional]);

  useEffect(() => {
    if (prevPersonTypeRef.current !== null && prevPersonTypeRef.current !== formPresenter.personType) {
      setFormPresenter(prev => ({
        ...prev,
        memberFirm: null,
        professional: null,
      }));
      prevFirmIdRef.current = null;
    }
    prevPersonTypeRef.current = formPresenter.personType;
  }, [formPresenter.personType]);

  const selectedFirmId = formPresenter?.memberFirm?.id || null;
  const selectedCompanyId = formPresenter?.personType === "guest" ? selectedFirmId : null;

  const availableProfessionals = useMemo(() => {
    return filteredProfessionals.filter((professional) => {
      return !presenters.some(
        (presenter) =>
          presenter.professional?.id === professional.id &&
          presenter.personType === formPresenter.personType
      );
    });
  }, [filteredProfessionals, presenters, formPresenter.personType]);

  const availableGuestSpeakers = useMemo(() => {
    return filteredGuestSpeakers.filter((guestSpeaker) => {
      return !presenters.some(
        (presenter) =>
          presenter.professional?.id === guestSpeaker.id &&
          presenter.personType === formPresenter.personType
      );
    });
  }, [filteredGuestSpeakers, presenters, formPresenter.personType]);

  const handleSavePresenter = useCallback(() => {
    if (!formPresenter.professional) {
      return;
    }
    
    if (!formPresenter.personType) {
      return;
    }
    
    const isDuplicate = presenters.some(
      (presenter) =>
        presenter.professional?.id === formPresenter.professional?.id &&
        presenter.personType === formPresenter.personType
    );
    
    if (isDuplicate) {
      toastError("This speaker has already been added with the same type. Please select a different speaker or remove the existing one.");
      return;
    }
    
    let memberFirm = formPresenter.memberFirm;
    
    if (formPresenter.personType === "guest") {
      if (memberFirm?.id && !memberFirm.name) {
        const company = companyOptions.find((c: { id: string; name: string }) => c.id === memberFirm?.id);
        if (company) {
          memberFirm = company;
        }
      }
      
      if (!memberFirm && formPresenter.memberFirm?.id) {
        const company = companyOptions.find((c: { id: string; name: string }) => c.id === formPresenter.memberFirm?.id);
        if (company) {
          memberFirm = company;
        }
      }
    } else {
      if (memberFirm?.id && !memberFirm.name) {
        const firm = firmOptions.find((f: { id: string; name: string }) => f.id === memberFirm?.id);
        if (firm) {
          memberFirm = firm;
        }
      }
      
      if (!memberFirm && formPresenter.professional) {
        const selectedProfessional = filteredProfessionals.find(p => p.id === formPresenter.professional?.id);
        if (selectedProfessional?.firm) {
          const firm = firmOptions.find((f: { id: string; name: string }) => f.id === selectedProfessional.firm);
          if (firm) {
            memberFirm = firm;
          }
        }
      }
      
      if (!memberFirm && formPresenter.memberFirm?.id) {
        const firm = firmOptions.find((f: { id: string; name: string }) => f.id === formPresenter.memberFirm?.id);
        if (firm) {
          memberFirm = firm;
        }
      }
    }
    
    const newPresenter = {
      personType: formPresenter.personType,
      memberFirm: memberFirm,
      professional: formPresenter.professional,
    };
    onPresentersChange([...presenters, newPresenter]);

    setFilteredProfessionals([]);
    setFilteredGuestSpeakers([]);
    setFormPresenter((prev) => ({
      ...prev,
      memberFirm: null,
      professional: null,
    }));
    prevFirmIdRef.current = null;
    setDropdownResetKey((key) => key + 1);
  }, [formPresenter, presenters, onPresentersChange, filteredProfessionals, firmOptions, companyOptions]);

  const handleRemovePresenter = useCallback((index: number) => {
    onPresentersChange(presenters.filter((_, i) => i !== index));
  }, [presenters, onPresentersChange]);

  const parseName = useCallback((fullName: string | undefined): { firstName: string; lastName: string } => {
    if (!fullName) {
      return { firstName: "", lastName: "" };
    }
    const parts = fullName.trim().split(/\s+/);
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: "" };
    }
    const lastName = parts[parts.length - 1];
    const firstName = parts.slice(0, -1).join(" ");
    return { firstName, lastName };
  }, []);

  const formatPresenterData = useMemo(() => {
    const completePresentersWithIndex = presenters
      .map((presenter, originalIndex) => ({ presenter, originalIndex }))
      .filter(({ presenter }) => presenter.professional && presenter.personType);
    
    return completePresentersWithIndex.map(({ presenter, originalIndex }, index) => {
      const { firstName, lastName } = parseName(presenter.professional?.name);
      
      let firmCompanyName = "-";
      if (presenter.memberFirm) {
        if (presenter.memberFirm.name) {
          firmCompanyName = presenter.memberFirm.name;
        } else if (presenter.memberFirm.id) {
          if (presenter.personType === "guest") {
            const company = companyOptions.find((c: { id: string; name: string }) => c.id === presenter.memberFirm?.id);
            firmCompanyName = company?.name || "-";
          } else {
            const firm = firmOptions.find((f: { id: string; name: string }) => f.id === presenter.memberFirm?.id);
            firmCompanyName = firm?.name || "-";
          }
        }
      } else if (presenter.professional?.id && presenter.personType !== "guest") {
        const professional = filteredProfessionals.find(p => p.id === presenter.professional?.id);
        if (professional?.firm) {
          const firm = firmOptions.find((f: { id: string; name: string }) => f.id === professional.firm);
          firmCompanyName = firm?.name || "-";
        }
      }
      
      return {
        id: originalIndex,
        number: index + 1,
        type: speakerTypeOptions.find(opt => opt.key === presenter.personType)?.value || presenter.personType,
        lastName: lastName || "-",
        firstName: firstName || "-",
        firmCompany: firmCompanyName,
        presenter: presenter,
      };
    });
  }, [presenters, speakerTypeOptions, parseName, firmOptions, companyOptions, filteredProfessionals]);

  const formatDataWithSorting = useCallback((data: any[]) => {
    if (!sort) {
      return data;
    }

    const sorted = [...data].sort((a, b) => {
      const field = sort.field;
      const order = sort.order;
      let aValue: any = a[field as keyof typeof a];
      let bValue: any = b[field as keyof typeof b];

      if (aValue === "-") {
        aValue = "";
      }
      if (bValue === "-") {
        bValue = "";
      }

      if (aValue === "" && bValue !== "") {
        return order === "asc" ? 1 : -1;
      }
      if (aValue !== "" && bValue === "") {
        return order === "asc" ? -1 : 1;
      }

      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue.localeCompare(bValue);
        return order === "asc" ? comparison : -comparison;
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return sorted.map((item, index) => ({
      ...item,
      number: index + 1,
    }));
  }, [sort]);

  const columnsDef = useMemo(() => [
    {
      id: "number",
      header: "#",
      enableSorting: false,
      cell: ({ row }: { rowIndex: number; row: any; value: any }) => (
        <p>{row.number}</p>
      ),
    },
    {
      id: "type",
      header: "Type",
      cell: ({ value }: { rowIndex: number; row: any; value: any }) => (
        <p>{value}</p>
      ),
    },
    {
      id: "lastName",
      header: "Last Name",
      cell: ({ value }: { rowIndex: number; row: any; value: any }) => (
        <p>{value}</p>
      ),
    },
    {
      id: "firstName",
      header: "First Name",
      cell: ({ value }: { rowIndex: number; row: any; value: any }) => (
        <p>{value}</p>
      ),
    },
    {
      id: "firmCompany",
      header: "Firm / Company",
      cell: ({ value }: { rowIndex: number; row: any; value: any }) => (
        <p>{value}</p>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      cell: ({ row }: { rowIndex: number; row: any; value: any }) => {
        const index = row.id;
        const handleDelete = (e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          handleRemovePresenter(index);
        };
        return (
          <div 
            onClick={handleDelete}
            className="flex items-center gap-2 cursor-pointer text-gray-font"
          >
            <span>Delete</span>
            <div 
              className="flex items-center justify-center w-4 h-4"
              style={{ color: '#474F56' } as React.CSSProperties}
            >
              <div className="w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg>path]:fill-current">
                <Trash />
              </div>
            </div>
          </div>
        );
      },
    },
  ], [handleRemovePresenter]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <h2 className="text-[#0070CD] font-decimal text-[32px] font-medium leading-[36px] text-left">
          {title}
        </h2>
      </div>

      <div className="space-y-4 bg-white p-4 rounded border">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-font font-Roboto">
            Add New Presenter
          </h3>
        </div>

        <div className="flex gap-6 flex-wrap mb-4">
          {speakerTypeOptions.map((option, optionIndex) => {
            const presenterTypeKey = option.key as keyof typeof SpeakerTypeEnum;
            const isSelected = formPresenter?.personType === presenterTypeKey;
            return (
              <RadioButton
                key={option.key}
                index={optionIndex}
                text={option.value}
                selected={isSelected}
                handleRadioButtonChange={() => {
                  setFormPresenter(prev => ({ ...prev, personType: presenterTypeKey }));
                }}
                name={`presenterType-${route}`}
              />
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <DropDownList
            key={`presenter-firm-${dropdownResetKey}`}
            title={formPresenter?.personType === "guest" ? "Company" : "Member Firm"}
            data={formPresenter?.personType === "guest" ? companyOptions : firmOptions}
            selectedObjst={formPresenter?.memberFirm?.name}
            selectedValue={formPresenter?.memberFirm ?? undefined}
            compareBy="id"
            seter={(value) => {
              const options = formPresenter?.personType === "guest" ? companyOptions : firmOptions;
              if (typeof value === "string") {
                const option = options.find((opt: { id: string; name: string }) => opt.name === value);
                setFormPresenter(prev => ({ ...prev, memberFirm: option || null, professional: null }));
              } else if (value && value.id) {
                setFormPresenter(prev => ({ ...prev, memberFirm: value, professional: null }));
              } else {
                setFormPresenter(prev => ({ ...prev, memberFirm: null, professional: null }));
              }
            }}
            error={false}
            placeholder={formPresenter?.personType === "guest" ? "Select Company" : "Select Member Firm"}
            getid={true}
          />

          <DropDownList
            key={`presenter-professional-${dropdownResetKey}`}
            title={formPresenter?.personType === "guest" ? "Guest Speaker" : "Professional"}
            data={formPresenter?.personType === "guest" ? availableGuestSpeakers : availableProfessionals}
            selectedObjst={formPresenter?.professional?.name}
            selectedValue={formPresenter?.professional ?? undefined}
            compareBy="id"
            seter={(value) => {
              const options = formPresenter?.personType === "guest" ? availableGuestSpeakers : availableProfessionals;
              if (typeof value === "string") {
                const option = options.find((opt: { id: string; name: string }) => opt.name === value);
                setFormPresenter(prev => ({ ...prev, professional: option || null }));
              } else if (value && value.id) {
                setFormPresenter(prev => ({ ...prev, professional: value }));
              } else {
                setFormPresenter(prev => ({ ...prev, professional: null }));
              }
            }}
            error={false}
            placeholder={
              formPresenter?.personType === "guest"
                ? selectedCompanyId
                  ? "Select Guest Speaker"
                  : "Select Company first"
                : selectedFirmId
                ? "Select Professional"
                : "Select Member Firm first"
            }
            getid={true}
            disabled={formPresenter?.personType === "guest" ? !selectedCompanyId : !selectedFirmId}
          />
        </div>

        <div className="flex justify-end">
          <LegacyButton
            text="ADD"
            content="textIcon"
            icon={<div className="w-5 h-5 [&>svg>path]:fill-current ml-2"><PlusIcon /></div>}
            color="accent"
            style="filled"
            size="small"
            onClick={handleSavePresenter}
          />
        </div>
      </div>

      {formatPresenterData.length > 0 && (
        <PaginatedTable
          data={formatPresenterData || []}
          columnsDef={columnsDef}
          formatData={formatDataWithSorting}
          sorting={sort || { field: "number", order: "asc" }}
          onSortingChange={setSort as any}
          totalCount={formatPresenterData.length || 0}
          pagination={pagination}
          onPaginationChange={setPagination}
          showExcelExport={false}
          showColumnVisibility={false}
        />
      )}
    </div>
  );
};

export default PresentersManager;

